import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, formatApiError } from "@/lib/api";
import { Brand, TopControls } from "@/components/Nav";
import Footer from "@/components/Footer";
import { openCookiePreferences } from "@/components/CookieBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, Upload, FileText, Loader2, Download } from "lucide-react";

const TYPES = ["Hotel", "B&B", "Appartamento", "Casa Vacanze", "Affittacamere", "Resort", "Altro"];

export default function AccessRequest() {
  const { lang } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const nav = useNavigate();
  const [f, setF] = useState({ applicant_name: "", owner_name: "", email: "", phone: "", structure_name: "",
    structure_type: "", address: "", website: "", booking_url: "", rooms: "", pms: "", vat_code: "", message: "" });
  const [consents, setConsents] = useState({ owner_authorized: false, data_lawful: false,
    processing_authorized: false, terms: false, privacy: false, dpa: false });
  const [requestRef, setRequestRef] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [file, setFile] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [pricing, setPricing] = useState(null);
  useEffect(() => {
    api.get("/public/pricing").then(({ data }) => setPricing(data)).catch(() => setPricing(null));
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const requiredFilled = f.applicant_name && f.email && f.phone && f.structure_name && f.structure_type && f.vat_code;
  const allConsents = Object.values(consents).every(Boolean);
  const canGenerate = requiredFilled && allConsents;
  const canSubmit = canGenerate && requestRef && file;

  const generatePdf = async () => {
    if (!canGenerate) { toast.error(L("Compila i campi obbligatori e accetta le dichiarazioni", "Fill required fields & accept declarations")); return; }
    setGenBusy(true);
    try {
      const res = await api.post("/auth/access-request/generate-pdf", f, { responseType: "blob" });
      const ref = res.headers["x-request-ref"] || res.headers["X-Request-Ref"] || "";
      setRequestRef(ref);
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setPdfUrl(url);
      const a = document.createElement("a");
      a.href = url; a.download = `modulo-autorizzazione-${ref || "revenueai"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(L("Modulo generato: firmalo e ricaricalo qui sotto", "Form generated: sign it and upload below"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setGenBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { toast.error(L("Completa tutti i passaggi", "Complete all steps")); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v || ""));
      fd.set("rooms", f.rooms || "1");
      fd.append("request_ref", requestRef);
      fd.append("consents", JSON.stringify(consents));
      fd.append("signed_doc", file);
      await api.post("/auth/access-request", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="request-done">
      <CheckCircle2 className="w-16 h-16 text-emerald-500 mb-4" />
      <h1 className="text-2xl font-bold mb-2">{L("Richiesta inviata!", "Request sent!")}</h1>
      <p className="text-muted-foreground max-w-md mb-2">{L("Grazie. Verificheremo i tuoi dati e il modulo firmato.", "Thank you. We'll verify your details and signed form.")}</p>
      <p className="text-muted-foreground max-w-md mb-6">{L("Dopo l'approvazione riceverai un'email con il link per scegliere l'abbonamento e completare il pagamento.", "Once approved, you'll receive an email with a link to choose your plan and complete payment.")}</p>
      {requestRef && <p className="text-xs font-mono text-muted-foreground mb-6">ID: {requestRef}</p>}
      <Link to="/login"><Button data-testid="back-to-login">{L("Torna al login", "Back to login")}</Button></Link>
    </div>
  );

  const field = (k, label, opts = {}) => (
    <div>
      <Label>{label}{opts.req && <span className="text-destructive"> *</span>}</Label>
      <Input data-testid={`ar-${k}`} type={opts.type || "text"} value={f[k]} onChange={set(k)} className="mt-1" required={opts.req} />
    </div>
  );

  const check = (k, label) => (
    <label className="flex items-start gap-2.5 text-sm cursor-pointer" data-testid={`ar-consent-${k}-label`}>
      <input type="checkbox" data-testid={`ar-consent-${k}`} checked={consents[k]}
        onChange={(e) => setConsents({ ...consents, [k]: e.target.checked })} className="mt-1 shrink-0" />
      <span className="text-muted-foreground">{label}</span>
    </label>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <div className="px-6 sm:px-10 py-10 max-w-3xl mx-auto w-full flex-1">
        <div className="flex items-center justify-between mb-8">
          <Brand onClick={() => nav("/")} /><TopControls />
        </div>
        <h1 className="text-3xl font-bold mb-1">{L("Richiedi accesso", "Request access")}</h1>
        <p className="text-muted-foreground mb-8">{L("Compila il modulo: nessun account viene creato automaticamente. Dopo la verifica e il pagamento attiveremo il tuo account e la tua struttura.", "Fill the form: no account is created automatically. After verification and payment we'll activate your account and structure.")}</p>

        {pricing && (Number(pricing.price_monthly) > 0 || Number(pricing.price_annual) > 0) && (
          <div className="mb-8 rounded-lg border border-primary/25 bg-primary/5 px-4 py-3" data-testid="ar-pricing">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 text-xs">
              {pricing.trial_enabled && Number(pricing.trial_days) > 0 && (
                <span className="inline-flex items-center gap-1.5 font-semibold text-primary" data-testid="ar-pricing-trial">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {Number(pricing.trial_days) >= 28
                    ? L("1 mese di prova gratuita", "1 month free trial")
                    : L(`${pricing.trial_days} giorni di prova gratuita`, `${pricing.trial_days}-day free trial`)}
                </span>
              )}
              {Number(pricing.price_monthly) > 0 && (
                <span className="text-muted-foreground" data-testid="ar-pricing-monthly">
                  {L("poi", "then")}{" "}
                  {Number(pricing.price_monthly_original) > Number(pricing.price_monthly) && (
                    <span className="line-through mr-1 opacity-70">€{Number(pricing.price_monthly_original)}</span>
                  )}
                  <span className="font-semibold text-foreground">€{Number(pricing.price_monthly)}</span>/{L("mese", "month")}
                </span>
              )}
              {Number(pricing.price_annual) > 0 && (
                <span className="text-muted-foreground" data-testid="ar-pricing-annual">
                  {L("oppure", "or")}{" "}
                  {Number(pricing.price_annual_original) > Number(pricing.price_annual) && (
                    <span className="line-through mr-1 opacity-70">€{Number(pricing.price_annual_original)}</span>
                  )}
                  <span className="font-semibold text-foreground">€{Number(pricing.price_annual)}</span>/{L("anno", "year")}
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">{L("La scelta del piano e il pagamento avvengono dopo l'approvazione della richiesta.", "Plan choice and payment happen after your request is approved.")}</p>
          </div>
        )}

        <form onSubmit={submit} className="space-y-8">
          {/* 1. Dati */}
          <section>
            <h2 className="text-base font-semibold mb-3">1. {L("Dati richiesta", "Request details")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("applicant_name", L("Nome e Cognome", "Full name"), { req: true })}
              {field("email", L("Email", "Email"), { req: true, type: "email" })}
              {field("phone", L("Telefono", "Phone"), { req: true })}
              {field("structure_name", L("Nome struttura", "Structure name"), { req: true })}
              <div>
                <Label>{L("Tipologia struttura", "Structure type")}<span className="text-destructive"> *</span></Label>
                <select data-testid="ar-structure_type" value={f.structure_type} onChange={set("structure_type")} required
                  className="mt-1 w-full h-10 rounded-md border border-border bg-background px-3 text-sm">
                  <option value="">—</option>{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              {field("vat_code", L("Partita IVA / Codice Fiscale", "VAT / Tax code"), { req: true })}
              {field("owner_name", L("Ragione sociale / Titolare", "Company / Owner"))}
              {field("rooms", L("Numero camere", "Rooms"), { type: "number" })}
              {field("address", L("Indirizzo struttura", "Structure address"))}
              {field("booking_url", L("Link Booking", "Booking link"))}
              {field("pms", L("PMS utilizzato", "PMS used"))}
              {field("website", L("Sito web (facoltativo)", "Website (optional)"))}
            </div>
            <div className="mt-4">
              <Label>{L("Messaggio (facoltativo)", "Message (optional)")}</Label>
              <textarea data-testid="ar-message" value={f.message} onChange={set("message")} rows={3}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </div>
          </section>

          {/* 2. Dichiarazioni */}
          <section>
            <h2 className="text-base font-semibold mb-3">2. {L("Dichiarazioni", "Declarations")}</h2>
            <div className="space-y-3 rounded-lg border border-border p-4">
              {check("owner_authorized", L("Dichiaro di essere il titolare della struttura oppure di essere stato autorizzato dal titolare ad utilizzare Revenue AI.", "I declare I am the owner of the structure or authorized by the owner to use Revenue AI."))}
              {check("data_lawful", L("Dichiaro che tutti i dati che caricherò sono raccolti e trattati nel rispetto della normativa vigente.", "I declare all data I will upload is collected and processed in compliance with applicable law."))}
              {check("processing_authorized", L("Autorizzo Revenue AI ad elaborare tali dati esclusivamente per fornire il servizio richiesto.", "I authorize Revenue AI to process such data solely to provide the requested service."))}
              <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <input type="checkbox" data-testid="ar-consent-terms" checked={consents.terms} onChange={(e) => setConsents({ ...consents, terms: e.target.checked })} className="mt-1 shrink-0" />
                <span className="text-muted-foreground">{L("Ho letto e accetto i", "I have read and accept the")} <Link to="/terms" target="_blank" className="text-primary hover:underline">{L("Termini di Servizio", "Terms of Service")}</Link>.</span>
              </label>
              <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <input type="checkbox" data-testid="ar-consent-privacy" checked={consents.privacy} onChange={(e) => setConsents({ ...consents, privacy: e.target.checked })} className="mt-1 shrink-0" />
                <span className="text-muted-foreground">{L("Ho letto l'", "I have read the")} <Link to="/privacy" target="_blank" className="text-primary hover:underline">{L("Informativa Privacy", "Privacy Policy")}</Link>.</span>
              </label>
              <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <input type="checkbox" data-testid="ar-consent-dpa" checked={consents.dpa} onChange={(e) => setConsents({ ...consents, dpa: e.target.checked })} className="mt-1 shrink-0" />
                <span className="text-muted-foreground">{L("Ho preso visione del", "I have reviewed the")} <Link to="/dpa" target="_blank" className="text-primary hover:underline">Data Processing Agreement (DPA)</Link>.</span>
              </label>
            </div>
          </section>

          {/* 3. Genera modulo */}
          <section>
            <h2 className="text-base font-semibold mb-3">3. {L("Modulo di autorizzazione", "Authorization form")}</h2>
            <div className="rounded-lg border border-border p-4">
              <p className="text-sm text-muted-foreground mb-3">{L("Genera il modulo precompilato con i tuoi dati, firmalo (fisicamente o digitalmente) e ricaricalo in PDF.", "Generate the pre-filled form with your data, sign it (physically or digitally) and upload it back as PDF.")}</p>
              <div className="flex flex-wrap items-center gap-3">
                <Button type="button" variant="outline" onClick={generatePdf} disabled={!canGenerate || genBusy} data-testid="ar-generate-pdf">
                  {genBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                  {L("Genera modulo di autorizzazione", "Generate authorization form")}
                </Button>
                {pdfUrl && <a href={pdfUrl} download={`modulo-autorizzazione-${requestRef}.pdf`} className="text-sm text-primary hover:underline inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" />{L("Scarica di nuovo", "Download again")}</a>}
              </div>
              {requestRef && <p className="text-xs text-muted-foreground mt-3">{L("ID richiesta", "Request ID")}: <span className="font-mono">{requestRef}</span></p>}
              {!canGenerate && <p className="text-xs text-amber-600 mt-3">{L("Compila i campi obbligatori (*) e seleziona tutte le dichiarazioni per generare il modulo.", "Fill required fields (*) and select all declarations to generate the form.")}</p>}
            </div>
          </section>

          {/* 4. Upload */}
          <section>
            <h2 className="text-base font-semibold mb-3">4. {L("Carica il modulo firmato", "Upload the signed form")}</h2>
            <label className={`flex items-center gap-2 border border-dashed rounded-md px-4 py-3 cursor-pointer transition-colors ${requestRef ? "hover:bg-accent/40 border-border" : "opacity-50 pointer-events-none border-border"}`} data-testid="ar-file-label">
              <Upload className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{file ? file.name : L("Carica il modulo firmato (solo PDF)", "Upload signed form (PDF only)")}</span>
              <input type="file" data-testid="ar-file" className="hidden" accept="application/pdf,.pdf" disabled={!requestRef}
                onChange={(e) => { const ff = e.target.files?.[0] || null; if (ff && !ff.name.toLowerCase().endsWith(".pdf")) { toast.error(L("Solo file PDF", "PDF only")); return; } setFile(ff); }} />
            </label>
          </section>

          <div className="flex items-center gap-3 pt-2">
            <Button data-testid="ar-submit" type="submit" disabled={!canSubmit || busy}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : L("Invia richiesta", "Send request")}</Button>
            <Link to="/login" className="text-sm text-muted-foreground hover:underline">{L("Ho già un account", "I already have an account")}</Link>
          </div>
        </form>
      </div>
      <Footer onOpenCookiePrefs={openCookiePreferences} />
    </div>
  );
}
